<template>
    <div class="filter-item-editor">
        <section class="header">
            <AttributeIconPreview :attribute="clone.attribute" />
            <span class="title">
                {{
                    `${getAttributeValue()}`.startsWith("akinon_") ||
                    `${getAttributeValue()}`.startsWith("probel_") ||
                    Attributes.includes(getAttributeValue()) ||
                    SystemAttributeValues.includes(getAttributeValue())
                        ? $t(`chatbotv2.attr.${getAttributeValue()}`)
                        : Entities.includes(getAttributeValue())
                            ? $t(`chatbotv2.${getAttributeValue()}`)
                            : clone.attribute.startsWith("contact_property_")
                                ? $_.get(visitorContactAttributes[clone.attribute.replace("contact_property_", "")], "label")
                                : getAttributeValue()
                }}
            </span>
            <md-button class="md-icon-button md-mini" @click="onClosed">
                <md-icon>close</md-icon>
            </md-button>
        </section>
        <section class="rules-section">
            <div class="title">{{ $t("chatbotv2.condition") }}</div>
            <div class="rules">
                <div
                    class="rule"
                    v-for="rule of getOperators()"
                    :key="rule.value"
                    :class="{ active: rule.value === clone.rule }"
                    @click="() => changeRule(rule.value)"
                >
                    <font-awesome-icon icon="equals" v-if="rule.value === 'equal'" />
                    <font-awesome-icon icon="not-equal" v-else-if="rule.value === 'not_equal'" />
                    <font-awesome-icon icon="less-than" v-else-if="rule.value === 'less_than'" />
                    <font-awesome-icon icon="less-than-equal" v-else-if="rule.value === 'less_or_equal'" />
                    <font-awesome-icon icon="greater-than" v-else-if="rule.value === 'greater_than'" />
                    <font-awesome-icon icon="greater-than-equal" v-else-if="rule.value === 'greater_or_equal'" />
                    {{ $t(`chatbotv2.filters.${rule.value}`) }}
                </div>
            </div>
        </section>
        <section class="value-section" v-if="clone.attribute === 'available_agents'">
            <div class="title">Department</div>
            <span>
                <a-select
                    :value="clone['departmentId']"
                    @change="changeDepartmentId"
                    class="select-department"
                    name="department"
                >
                    <a-select-option :value="''" key="all"> All </a-select-option>
                    <a-select-option v-for="department of subjects" :value="department._id" :key="department._id">
                        {{ getDepartmentTitle(department) }}
                    </a-select-option>
                </a-select>
            </span>
        </section>
        <section
            class="value-section"
            v-if="clone.attribute === 'channelId' && !['empty', 'non_empty'].includes(clone.rule)"
        >
            <div class="title">Channel</div>
            <span>
                <a-select :value="clone.value" @change="(e) => (clone.value = e)" class="select-channel">
                    <a-select-option v-for="channel of channels" :value="channel._id" :key="channel._id"
                    >{{ channel.name }}
                    </a-select-option>
                </a-select>
            </span>
        </section>
        <section class="value-section" v-else-if="clone.attribute === 'segments' && clone.rule !== 'empty'">
            <div class="title">Value</div>
            <SegmentPicker v-model="clone.value" @input="changeSegmentId" />
        </section>
        <section class="value-section" v-else-if="clone.attribute === 'tags' && clone.rule !== 'empty'">
            <div class="title">Value</div>
            <TagPicker v-model="clone.value" />
        </section>
        <!--        <section class="value-section" v-else-if="clone.attribute === 'ticket'">-->
        <!--            <div class="title">Ticket</div>-->
        <!--            <TagDialog v-model="clone.value" @input="changeTicket" />-->
        <!--        </section>-->

        <section class="value-section" v-else-if="!['empty', 'non_empty'].includes(clone.rule)">
            <div class="title">{{ $t("chatbotv2.value") }}</div>
            <input
                v-if="isNumericAttribute()"
                required="required"
                type="number"
                placeholder="Value"
                :min="-9007199254740991"
                :max="9007199254740991"
                :step="1"
                :class="{ error: isValueValid(), focused: inputFocused }"
                v-model="clone.value"
                @focus="inputFocused = true"
                @blue="inputFocused = false"
            />
            <span v-else-if="isBoolean()">
                <a-select
                    :value="clone.value + ''"
                    @change="
                        (e) => {
                            clone.value = e;
                        }
                    "
                    class="select-attribute"
                >
                    <a-select-option
                        :value="booleanValue.value + ''"
                        :key="`${booleanValue.label}-${booleanValue.value}`"
                        v-for="booleanValue of getBooleanValues()"
                    >{{ booleanValue.label }}
                    </a-select-option>
                </a-select>
            </span>
            <span v-else-if="isSelect()">
                <a-select
                    :value="clone.value"
                    @change="
                        (e) => {
                            clone.value = e;
                        }
                    "
                    class="select-attribute"
                >
                    <a-select-option :value="opt.value" :key="opt.value" v-for="opt of getOptions()"
                    >{{ opt.label }}
                    </a-select-option>
                </a-select>
            </span>
            <a-select
                v-else-if="clone.attribute === 'akinon_order_status'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-akinon"
            >
                <a-select-option
                    :value="status"
                    :key="status"
                    v-for="status of Object.keys(AKINON_ORDER_STATUSES).map((x) => AKINON_ORDER_STATUSES[x])"
                >{{ status }}
                </a-select-option>
            </a-select>
            <a-select
                v-else-if="clone.attribute === 'ideasoft_order_status'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-akinon"
            >
                <a-select-option
                    :value="key.toLowerCase()"
                    :key="key"
                    v-for="key of Object.keys(IDEASOFT_ORDER_STATUSES)"
                >{{ IDEASOFT_ORDER_STATUSES[key] }}
                </a-select-option>
            </a-select>
            <a-select
                v-else-if="clone.attribute === 'department'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-department"
            >
                <a-select-option v-for="department of subjects" :value="department._id" :key="department._id"
                >{{ getDepartmentTitle(department) }}
                </a-select-option>
            </a-select>
            <a-select
                v-else-if="clone.attribute === 'channelId'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-channel"
            >
                <a-select-option v-for="channel of channels" :value="channel._id" :key="channel._id"
                >{{ channel.name }}
                </a-select-option>
            </a-select>

            <a-select
                v-else-if="clone.attribute === 'owner'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-channel"
            >
                <a-select-option v-for="user of users" :value="user._id" :key="user._id"
                >{{ user.name }}
                </a-select-option>
            </a-select>

            <a-select
                v-else-if="clone.attribute === 'visitor_trigger_type'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-channel"
            >
                <a-select-option :value="'automation'" :key="'automation'">Automation</a-select-option>
                <a-select-option :value="'broadcast'" :key="'broadcast'">Broadcast</a-select-option>
            </a-select>
            <a-select
                v-else-if="isSelectableContactProperty()"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-channel"
            >
                <a-select-option v-for="option of getPropertyList()" :value="option" :key="option">
                    {{ option }}
                </a-select-option>
            </a-select>
            <TiptapEditor
                v-else
                :show-emoji="true"
                :show-attributes="true"
                :placeholder="$t('chatbotv2.value')"
                :classNames="[ isValueValid() && 'error',  inputFocused && 'focused' ].join(' ')"
                :max-character-count="6200"
                v-model="clone.value"
                @focus="inputFocused = true"
                @blue="inputFocused = false"
            />
        </section>
        <section class="apply">
            <a-button
                type="primary"
                :disabled="!['empty', 'non_empty'].includes(clone.rule) && isValueValid()"
                @click="submit"
            >
                {{ $t("chatbotv2.applyFilter") }}
            </a-button>
        </section>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import _ from "lodash";
import AttributeIconPreview from "../../common/AttributeIconPreview";
import systemAttributes from "../../../../utils/systemAttributes";
import filterOperators from "../../../../utils/filterOperators";
import SegmentPicker from "../../pickers/SegmentPicker.vue";
import TagPicker from "../../pickers/TagPicker.vue";
import TagDialog from "../../../../../../../components/TagDialog.vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faEquals,
    faGreaterThan,
    faGreaterThanEqual,
    faLessThan,
    faLessThanEqual,
    faNotEqual,
} from "@fortawesome/free-solid-svg-icons";
import CONFIG from "../../../../../../../config/config";
import { Attributes, Entities } from "../../../../palatteEnums";
import { mapActions, mapState } from "vuex";
import { CHATBOT_API } from "../../../../../../../api/chatbotv2";
import TiptapEditor from "../../common/TiptapEditor.vue";

library.add(faEquals, faNotEqual, faLessThan, faLessThanEqual, faGreaterThan, faGreaterThanEqual);
const log = log2("SingleFilterEditor");
export default {
    name: "SingleFilterEditor",
    components: {
        TiptapEditor,
        AttributeIconPreview,
        SegmentPicker,
        TagPicker,
        TagDialog,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                rule: "greater_or_equal",
                attribute: "email",
                value: 0,
                departmentId: undefined,
            }),
        },
        onClosed: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            inputFocused: false,
            AKINON_ORDER_STATUSES: CONFIG.AKINON.ORDER_STATUSES,
            IDEASOFT_ORDER_STATUSES: CONFIG.IDEASOFT.ORDER_STATUSES,
            Attributes,
            Entities,
            SystemAttributeValues: systemAttributes.map((attr) => attr.value),
            window,
            aiFunctions: [],
            storyId: this.$route.params.id,
        };
    },
    computed: {
        ...mapState({
            subjects: (state) => state.subject.subjects.data,
            channels: (state) => state.channel.channels,
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((acc, property) => {
                    acc[property._id] = property;
                    return acc;
                }, {}),
            users: (state) => state.user.users.data,
        }),
    },
    methods: {
        ...mapActions({
            getSubjects: "subject/getSubjects",
        }),
        isSelectableContactProperty() {
            const isContactProperty = this.clone.attribute.startsWith("contact_property");
            const isDropdownProperty =
                isContactProperty &&
                this.visitorContactAttributes[this.clone.attribute.replace("contact_property_", "")].type ===
                "dropdown";
            return isDropdownProperty;
        },
        getPropertyList() {
            let options = _.get(
                this.visitorContactAttributes[this.clone.attribute.replace("contact_property_", "")],
                "options",
                [],
            );
            if (Array.isArray(options)) {
                return options;
            }

            return [];
        },

        getAIFunctionAttributes() {
            const items = this.aiFunctions;

            const attrs = items
                .map((fn) => {
                    return (fn.parameters || []).map((param) => {
                        const { name, type, description } = param;
                        const enumValues = param["enum"];
                        const key = `ai_function_parameter_${fn.name}___${name}`;
                        return {
                            name: `Function(${fn.name}) - Parameter(${name})`,
                            placeholder: "",
                            value: key,
                            type: "ai-function",
                            // always: false,
                            // filterMode: true,
                            special: true,
                            defaultOperator: "equal",
                            defaultFilterValue: Array.isArray(enumValues) && enumValues.length ? enumValues[0] : "",
                            controlType: Array.isArray(enumValues) && enumValues.length ? "select" : undefined,
                            options: enumValues.map((x) => ({ label: x, value: x })),
                            valueType: type,
                            description,
                        };
                    });
                })
                .flat();
            const result = [
                {
                    name: "YZ Fonksiyon",
                    placeholder: "Sipariş Oluştur",
                    value: "ai_function_name",
                    type: "ai-function",
                    defaultOperator: "equal",
                    defaultFilterValue: items[0]?.name || "",
                    // always: false,
                    // filterMode: true,
                    special: true,
                    availableOperators: ["equal", "not_equal"],
                    controlType: "select",
                    options: items.map((x) => ({ label: x.name, value: x.name })),
                },
                ...attrs,
            ];
            console.log("getAIFunctionAttributes", { result });
            return result;
        },
        fetchAIFunctions() {
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "aiFunction", 1, 100).then((res) => {
                this.aiFunctions = res.data.rows.map((x) => x.aiFunction);
            });
        },
        changeDepartmentId(departmentId) {
            log("changeDepartmentId", departmentId);
            this.clone.departmentId = departmentId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        changeChannelId(channelId) {
            log("changeChannelId", channelId);
            this.clone.channelID = channelId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        changeSegmentId(segmentId) {
            log("changeSegmentId", segmentId);
            this.clone.segmentId = segmentId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        // changeTicket() {
        //     log("changeTicket", this.clone.tags);
        //     this.clone.tags = this.clone.tags;
        //     this.clone = _.cloneDeep(this.clone);
        //     this.$nextTick();
        // },
        changeRule(rule) {
            this.clone.rule = rule;
        },
        onValueChanged(e) {
            log("onValueChanged", e.target.value);
            this.clone.value = e.target.value;
        },
        getAttributeValue() {
            log("getAttributeValue", this.value.attribute);
            if (this.value.attribute.startsWith("akinon_")) {
                const foundAttr = CONFIG.AKINON.attributes.find((attr) => attr.value === this.value.attribute);
                return _.get(foundAttr, "value", this.value.attribute);
            } else if (this.value.attribute.startsWith("probel_")) {
                const foundAttr = CONFIG.PROBEL.attributes.find((attr) => attr.value === this.value.attribute);
                return _.get(foundAttr, "value", this.value.attribute);
            } else if (this.value.attribute.startsWith("ideasoft_")) {
                const foundAttr = CONFIG.IDEASOFT.attributes.find((attr) => attr.value === this.value.attribute);
                return _.get(foundAttr, "value", this.value.attribute);
            } else {
                const foundAttr = systemAttributes.find((x) => x.value === this.value.attribute);
                return _.get(foundAttr, "value", this.value.attribute);
            }
        },
        getAttributeMeta() {
            if (this.value.attribute.startsWith("akinon_")) {
                return CONFIG.AKINON.attributes.find((attr) => attr.value === this.value.attribute);
            } else if (this.value.attribute.startsWith("probel_")) {
                return CONFIG.PROBEL.attributes.find((attr) => attr.value === this.value.attribute);
            } else if (this.value.attribute.startsWith("ideasoft_")) {
                return CONFIG.IDEASOFT.attributes.find((attr) => attr.value === this.value.attribute);
            } else if (this.value.attribute.startsWith("ai_function")) {
                const attrs = this.getAIFunctionAttributes();
                return attrs.find((attr) => attr.value === this.value.attribute);
            } else if (this.value.attribute.startsWith("contact_property")) {
                const contactProperties = Object.values(this.visitorContactAttributes);
                const defaultOperators = ["equal", "not_equal"];
                const availableOperatorsByType = {
                    text: ["equal", "not_equal", "contains", "not_contains", "empty", "non_empty"],
                    dropdown: ["equal", "not_equal"],
                    email: ["equal", "not_equal", "empty", "non_empty"],
                    phone: ["equal", "not_equal", "empty", "non_empty"],
                    number: ["equal", "not_equal", "less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                    url: ["equal", "not_equal", "empty", "non_empty"],
                    date: ["equal", "not_equal", "less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                };
                const attributes = contactProperties.map((x) => ({
                    name: x.label,
                    placeholder: "",
                    value: "contact_property_" + x._id,
                    type: "visitorContactProperty",
                    availableOperators: availableOperatorsByType[x.type] || defaultOperators,
                    defaultFilterValue: "",
                }));
                return attributes.find((x) => x.value === this.value.attribute);
            } else {
                return systemAttributes.find((x) => x.value === this.value.attribute);
            }
        },
        getOperatorName() {
            const foundOperator = filterOperators.find((x) => x.value === this.value.rule);
            log("getOperatorName", foundOperator, this.value.rule, filterOperators);
            return _.get(foundOperator, "label", this.value.rule);
        },
        getOperators() {
            const isNumericMode = this.isNumericAttribute();
            const isBoolean = this.isBoolean();
            const meta = this.getAttributeMeta();
            const isAvailableOperatorsExist = _.get(meta, "availableOperators", []).length > 0;
            if (isBoolean) {
                return filterOperators.filter((op) => !!op.isBooleanOperator);
            } else if (isAvailableOperatorsExist) {
                const availableOperators = _.get(meta, "availableOperators", []);
                return filterOperators.filter((op) => availableOperators.includes(op.value));
            }
            return filterOperators.filter((op) => (isNumericMode ? op.isNumeric : true));
        },
        isNumericAttribute() {
            const aiFunctionAttributes = this.getAIFunctionAttributes();
            const numericAttribute = ["score", "failures"];
            const isNumericMode =
                numericAttribute.includes(this.clone.attribute) ||
                aiFunctionAttributes.find((x) => x.value === this.clone.attribute && x.valueType === "number");
            return isNumericMode;
        },
        isBoolean() {
            const aiFunctionAttributes = this.getAIFunctionAttributes();
            const booleanAttributes = ["offline_mode", "available_agents", "ticket"];
            const isBoolean =
                booleanAttributes.includes(this.clone.attribute) ||
                aiFunctionAttributes.find((x) => x.value === this.clone.attribute && x.valueType === "boolean");
            return isBoolean;
        },

        isSelect() {
            const aiFunctionAttributes = this.getAIFunctionAttributes();
            return (
                !!systemAttributes.find((x) => x.value === this.clone.attribute && x.controlType === "select") ||
                !!CONFIG.PROBEL.attributes.find(
                    (x) => x.value === this.clone.attribute && x.controlType === "select",
                ) ||
                !!aiFunctionAttributes.find((x) => x.value === this.clone.attribute && x.controlType === "select")
            );
        },
        getOptions() {
            const aiFunctionAttributes = this.getAIFunctionAttributes();
            const attr =
                systemAttributes.find((x) => x.value === this.clone.attribute && x.controlType === "select") ||
                CONFIG.PROBEL.attributes.find((x) => x.value === this.clone.attribute && x.controlType === "select") ||
                aiFunctionAttributes.find((x) => x.value === this.clone.attribute && x.controlType === "select");

            return _.get(attr, "options", []) || [];
        },
        getBooleanValues() {
            if (this.clone.attribute === "offline_mode") {
                return [
                    {
                        label: "Active",
                        value: "true",
                    },
                    {
                        label: "Passive",
                        value: "false",
                    },
                ];
            }
            return [
                {
                    label: "True",
                    value: "true",
                },
                {
                    label: "False",
                    value: "false",
                },
            ];
        },
        isValueValid() {
            return ["empty", "non_empty"].includes(this.clone.rule) || _.isEmpty(this.clone.value);
        },
        submit() {
            this.$emit("input", this.clone);
            this.onClosed();
        },
        getDepartmentTitle(department) {
            const langs = Object.keys(department.languageContent);
            const targetLang = langs.includes(this.$i18n.locale) ? this.$i18n.locale : langs[0];
            return _.get(department, `languageContent.${targetLang}.name`);
        },
    },
    mounted() {
        log("mounted", this.clone);
        if (["available_agents", "department", "segments", "ticket"].includes(this.clone.attribute)) {
            if (this.subjects.length === 0) {
                this.getSubjects();
            }
        }
        this.fetchAIFunctions();
    },
    watch: {},
};
</script>

<style lang="scss">
.filter-item-editor {
    grid-row-gap: 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    max-width: 90vw;
    padding: 20px;
    width: 380px;

    .editable-editor-wrapper {
        border: 1px solid var(--border-neutral-default) !important;
    }

    .select-akinon,
    .select-department,
    .select-channel,
    .select-attribute {
        width: 100%;
    }

    section {
        .title {
            margin-bottom: 5px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }
    }

    section.header {
        align-items: center;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 15px;

        .title {
            margin-bottom: 0px;
        }

        .material-icons {
            color: #006cff;
            font-size: 25px;
            margin-right: 10px;
        }
    }

    section.rules-section {
        .rules {
            display: flex;
            flex-wrap: wrap;
            margin: -5px;

            .rule {
                &.active,
                &:hover {
                    background: #006cff;
                    color: #fff;
                    cursor: default;
                }

                align-items: center;
                background: #f0f4f7;
                border-radius: 6px;
                display: flex;
                font-size: 15px;
                font-weight: 600;
                line-height: 19px;
                margin: 5px;
                padding: 10px 15px;
                transition: all 0.3s;
                cursor: pointer;

                i,
                svg {
                    font-size: 10px;
                    margin-right: 7px;
                }
            }
        }
    }

    section.value-section {
        .editable-editor-wrapper {
            &.error {
                border-color: #fb6e6e !important;
            }

            &.focused,
            &:hover {
                border-color: #008aff !important;;
            }
        }
    }

    section.apply {
        .ant-btn {
            min-width: 80px;
            color: #fff;
            height: 37px;
            line-height: 35px;
            width: 100%;
            border-radius: 4px;
            display: inline-block;
            font-family: Source Sans Pro, sans-serif;
            font-weight: 600;
            padding: 0 10px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s;

            &[disabled] {
                background: #f5f8fb;
                border: 1px solid #f5f8fb;
                color: #919ead;
                cursor: not-allowed;
            }
        }
    }
}
</style>
